import { publishedListRule } from "./published-list";

/**
 * The rules as the code actually applies them.
 *
 * Every number is read off the exam or off a served constant, so a cap or a word
 * floor that moves cannot leave the copy promising the old one. Shared because
 * two surfaces state the same rules -- the packet a participant sits, and the
 * exam tab on the public board -- and a second copy is a second thing to drift.
 */

/**
 * Structural rather than the tRPC exam type: the public board serves the same
 * figures without the participant-only half of that payload.
 */
export interface ScoringRulesInput {
  prelist: readonly { kind: "EXAMPLE" | "RISK" }[];
  fields: readonly { label: string; minWords: number }[];
  submissionCap: number;
  verdictsPerFinding: number;
  reviewsPerFinding: number;
}

export function scoringRules(exam: ScoringRulesInput): [string, string][] {
  return [
    // Worded off the shape of the list rather than fixed, because an exam
    // publishing example findings and one publishing a risk list are refusing
    // different things: the same defect found twice, against the same class of
    // error named twice.
    publishedListRule(exam.prelist),
    ["Every field has a length floor", floorRule(exam.fields)],
    [
      "A hedge is not a condition",
      "A field that leans on a phrase like “sometimes” or “at scale” while saying little else is refused on the same pass.",
    ],
    [
      `At most ${String(exam.submissionCap)} findings, in the order you submit them`,
      "Anything past the cap is dropped by submission order, not by which ones read best. Two you would defend beat eight you thought of.",
    ],
    [
      "The same title twice is one finding",
      "A repeat of a title already used in your submission is refused, and the reason names the finding it repeats.",
    ],
    [
      `${String(exam.verdictsPerFinding)} other people rule on each finding`,
      `They are not told who wrote it, and they are asked whether it is real, never whether it is clever. You owe ${String(exam.reviewsPerFinding)} reviews for every finding of yours that lands.`,
    ],
  ];
}

/**
 * The floors, named per field when they differ.
 *
 * A field set does not have to be uniform: CHANNEL asks eight words of the
 * access route and five of the week-one signal. One number taken across the set
 * would state a floor Gate 1 does not apply, and the field asking for more is
 * exactly the one a finding is lost to.
 */
function floorRule(fields: ScoringRulesInput["fields"]): string {
  const refused =
    "A field under its floor is refused before a person reads it.";
  const floors = new Set(fields.map((field) => field.minWords));
  const [shared] = floors;

  if (floors.size === 1 && shared !== undefined) {
    return `Each of the ${String(fields.length)} fields needs at least ${String(shared)} words. ${refused}`;
  }
  const each = fields
    .map((field) => `“${field.label}” needs ${String(field.minWords)} words`)
    .join(", ");
  return `The floors differ by field: ${each}. ${refused}`;
}
